import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import CreatableSelect from "react-select/creatable";
import { NotificationService } from "../../api/generated/services/NotificationService";
import {
  AudienceFormState,
  EMPTY_AUDIENCE_FORM,
  fromOptionsViewModel,
  toOptionsDto,
} from "../../helpers/segmentedPushOptions";
import { Button } from "../Button";
import { TextField } from "../TextField";
import { Checkbox } from "../Checkbox";
import { Loader } from "../Loader";
import "../../api/openApiConfig";

interface NotificationFormModalProps {
  isOpen: boolean;
  /** null for a new notification, an id when editing an existing one */
  notificationId: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <h4 className="text-sm font-medium mt-2">{children}</h4>;

export const NotificationFormModal: React.FC<NotificationFormModalProps> = ({
  isOpen,
  notificationId,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState<AudienceFormState>(
    EMPTY_AUDIENCE_FORM
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setError(null);
    if (!notificationId) {
      setName("");
      setMessage("");
      setAudience(EMPTY_AUDIENCE_FORM);
      return;
    }

    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const details =
          await NotificationService.postApiNotificationGetNotification({
            notificationId,
          });
        if (ignore) return;
        setName(details.name ?? "");
        setMessage(details.message ?? "");
        setAudience(fromOptionsViewModel(details.options));
      } catch (err) {
        if (ignore) return;
        console.error("Error fetching notification details:", err);
        setError("Failed to load the notification.");
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [isOpen, notificationId]);

  const setAudienceField = <K extends keyof AudienceFormState>(
    field: K,
    value: AudienceFormState[K]
  ) => setAudience((previous) => ({ ...previous, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !message.trim()) {
      setError("Name and message are required.");
      return;
    }

    // An enabled user filter with no users silently matches nobody
    if (audience.usersEnabled && audience.userIds.length === 0) {
      setError('Add at least one user ID, or disable "Specific users only".');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const options = toOptionsDto(audience);
      if (notificationId) {
        await NotificationService.postApiNotificationUpdateNotification({
          notificationId,
          name,
          message,
          options,
        });
      } else {
        await NotificationService.postApiNotificationAddNotification({
          name,
          message,
          options,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error("Error saving notification:", err);
      setError(
        err?.body?.message || err?.message || "Failed to save the notification."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="modal-content md:max-w-[700px]"
      overlayClassName="modal-overlay"
      ariaHideApp={false}
    >
      <h3 className="text-lg font-medium mb-4">
        {notificationId ? "Edit notification" : "New notification"}
      </h3>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <TextField
            label="Name"
            placeholder="Internal campaign name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
          />
          <TextField
            as="textarea"
            label="Message"
            placeholder="Push message shown to the customer"
            rows={3}
            value={message}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setMessage(e.target.value)
            }
          />

          <div className="border-t border-gray-200 pt-4 space-y-4">
            <h4 className="text-base font-medium">Audience</h4>
            <p className="text-xs text-gray-400">
              All enabled filters must match. With no filters enabled the push
              goes to every registered device.
            </p>

            <div className="space-y-2">
              <Checkbox
                label="Filter by platform"
                checked={audience.osEnabled}
                onChange={(e) => setAudienceField("osEnabled", e.target.checked)}
              />
              {audience.osEnabled && (
                <div className="flex gap-6 pl-8">
                  <Checkbox
                    label="Android"
                    checked={audience.includeAndroid}
                    onChange={(e) =>
                      setAudienceField("includeAndroid", e.target.checked)
                    }
                  />
                  <Checkbox
                    label="iOS"
                    checked={audience.includeIos}
                    onChange={(e) =>
                      setAudienceField("includeIos", e.target.checked)
                    }
                  />
                  <Checkbox
                    label="Web"
                    checked={audience.includeWeb}
                    onChange={(e) =>
                      setAudienceField("includeWeb", e.target.checked)
                    }
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Checkbox
                label="Filter by payment card"
                checked={audience.cardEnabled}
                onChange={(e) =>
                  setAudienceField("cardEnabled", e.target.checked)
                }
              />
              {audience.cardEnabled && (
                <div className="pl-8">
                  <Checkbox
                    label="Has a payment card added"
                    checked={audience.hasCardAdded}
                    onChange={(e) =>
                      setAudienceField("hasCardAdded", e.target.checked)
                    }
                  />
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Checkbox
                label="Filter by balance"
                checked={audience.balanceEnabled}
                onChange={(e) =>
                  setAudienceField("balanceEnabled", e.target.checked)
                }
              />
              {audience.balanceEnabled && (
                <div className="pl-8 space-y-2">
                  <div className="flex gap-4">
                    <TextField
                      label="Minimum (kr)"
                      type="number"
                      placeholder="0"
                      value={audience.balanceMin}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setAudienceField("balanceMin", e.target.value)
                      }
                    />
                    <TextField
                      label="Maximum (kr)"
                      type="number"
                      placeholder="0"
                      value={audience.balanceMax}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setAudienceField("balanceMax", e.target.value)
                      }
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Available GrooKroner balance; pending cashback does not
                    count. Set both to 0 to target customers with an empty
                    balance.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Checkbox
                label="Filter by last purchase"
                checked={audience.lastPurchaseEnabled}
                onChange={(e) =>
                  setAudienceField("lastPurchaseEnabled", e.target.checked)
                }
              />
              {audience.lastPurchaseEnabled && (
                <div className="pl-8 space-y-2">
                  <div className="flex gap-4">
                    <div>
                      <label className="mb-0.5 block text-sm">
                        After (UTC)
                      </label>
                      <input
                        type="datetime-local"
                        className="flex w-full rounded-2xl border border-gray-300 bg-transparent px-4 py-[0.8438rem] text-sm leading-none focus-visible:outline-none focus-visible:ring-0"
                        value={audience.lastPurchaseAfter}
                        onChange={(e) =>
                          setAudienceField("lastPurchaseAfter", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="mb-0.5 block text-sm">
                        Before (UTC)
                      </label>
                      <input
                        type="datetime-local"
                        className="flex w-full rounded-2xl border border-gray-300 bg-transparent px-4 py-[0.8438rem] text-sm leading-none focus-visible:outline-none focus-visible:ring-0"
                        value={audience.lastPurchaseBefore}
                        onChange={(e) =>
                          setAudienceField("lastPurchaseBefore", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400">
                    Matches the customer's most recent cashback-earning
                    purchase, so it requires at least one purchase ever. Set
                    only "Before" to 30 days ago to target customers who have
                    not purchased since. The dates are fixed — adjust them
                    before every send.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Checkbox
                label="Specific users only"
                checked={audience.usersEnabled}
                onChange={(e) =>
                  setAudienceField("usersEnabled", e.target.checked)
                }
              />
              {audience.usersEnabled && (
                <div className="pl-8">
                  <SectionHeading>User IDs</SectionHeading>
                  <CreatableSelect
                    isMulti
                    placeholder="Type a user ID and press Enter..."
                    value={audience.userIds.map((id) => ({
                      label: id,
                      value: id,
                    }))}
                    onChange={(newValue) =>
                      setAudienceField(
                        "userIds",
                        newValue.map((v) => v.value)
                      )
                    }
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={saving}>
              {notificationId ? "Save changes" : "Create notification"}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};
